import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { InstallPWABanner } from "@/components/InstallPWABanner";
import { UpdateNotification } from "@/components/UpdateNotification";
import LoginPage from "@/pages/LoginPage";
import SetupPasswordPage from "@/pages/SetupPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import MemberDashboardPage from "@/pages/MemberDashboardPage";
import MembersPage from "@/pages/MembersPage";
import MemberDetailPage from "@/pages/MemberDetailPage";
import AdminStatusPage from "@/pages/AdminStatusPage";
import AdminManagePage from "@/pages/AdminManagePage";
import AdminChangesPage from "@/pages/AdminChangesPage";
import AdminNewsletterPage from "@/pages/AdminNewsletterPage";
import AdminEmailTemplatesPage from "@/pages/AdminEmailTemplatesPage";
import AdminDirectMailPage from "@/pages/AdminDirectMailPage";
import EditProfilePage from "@/pages/EditProfilePage";
import VehiclesPage from "@/pages/VehiclesPage";
import EventsPage from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import EventEditPage from "@/pages/EventEditPage";
import EventCheckinPage from "@/pages/EventCheckinPage";
import EventCheckinListPage from "@/pages/EventCheckinListPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceSettingsPage from "@/pages/InvoiceSettingsPage";
import MyInvoicesPage from "@/pages/MyInvoicesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminAGBPage from "@/pages/AdminAGBPage";
import AGBPage from "@/pages/AGBPage";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import SupportPage from "@/pages/SupportPage";
import MembershipRequestPage from "@/pages/MembershipRequestPage";
import CarOfTheMonthPage from "@/pages/CarOfTheMonthPage";
import MemberVehiclesPage from "@/pages/MemberVehiclesPage";
import VehicleDetailPage from "@/pages/VehicleDetailPage";
import AdminMembershipRequestsPage from "@/pages/AdminMembershipRequestsPage";
import AdminMemberMovementsPage from "@/pages/AdminMemberMovementsPage";
import AdminNewsletterSubscriptionsPage from "@/pages/AdminNewsletterSubscriptionsPage";
import NewsletterCampaignsPage from "@/pages/NewsletterCampaignsPage";
import NewsletterEditorPage from "@/pages/NewsletterEditorPage";
import NewsletterAnalyticsPage from "@/pages/NewsletterAnalyticsPage";
import NewsletterSubscribersPage from "@/pages/NewsletterSubscribersPage";
import NewsletterReportPage from "@/pages/NewsletterReportPage";
import PublicEventsPage from "@/pages/PublicEventsPage";
import AdminDocumentsPage from "@/pages/AdminDocumentsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import AboutPage from "@/pages/AboutPage";
import InstallGuidePage from "@/pages/InstallGuidePage";
import AdminSyncPage from "@/pages/AdminSyncPage";
import AdminActivityLogPage from "@/pages/AdminActivityLogPage";
import NewsletterGroupsPage from "@/pages/NewsletterGroupsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import GuestSuccessPage from "@/pages/GuestSuccessPage";
import PublicEventDetailPage from "@/pages/PublicEventDetailPage";
import MyOrganizedEventsPage from "@/pages/MyOrganizedEventsPage";
import SurveysPage from "@/pages/SurveysPage";
import SurveyEditPage from "@/pages/SurveyEditPage";
import SurveyResultsPage from "@/pages/SurveyResultsPage";
import PublicSurveyPage from "@/pages/PublicSurveyPage";
import PublicListingPage from "@/pages/PublicListingPage";
import MarketplacePage from "@/pages/MarketplacePage";
import ListingEditPage from "@/pages/ListingEditPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import MessagesPage from "@/pages/MessagesPage";
import StripeFinancePage from "@/pages/StripeFinancePage";

// Newsletter tracking redirect: forwards /api/newsletter/track/* to the actual backend
function NewsletterTrackRedirect() {
  const backendUrl = 'https://shrt-backend.onrender.com';
  React.useEffect(() => {
    window.location.replace(backendUrl + window.location.pathname + window.location.search);
  }, []);
  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Laden...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Laden...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const [searchParams] = useSearchParams();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Laden...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Check for return URL first
    const returnUrl = searchParams.get('returnUrl') || searchParams.get('return');
    if (returnUrl) {
      return <Navigate to={returnUrl} replace />;
    }
    // Redirect admins to dashboard, members to home (member dashboard)
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/api/newsletter/track/*" element={<NewsletterTrackRedirect />} />
      <Route path="/agb" element={<AGBPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/install" element={<InstallGuidePage />} />
      <Route path="/mitglied-werden" element={<MembershipRequestPage />} />
      <Route path="/public/events" element={<PublicEventsPage />} />
      <Route path="/embed/events" element={<PublicEventsPage />} />
      <Route path="/public/event/:eventId" element={<PublicEventDetailPage />} />
      <Route path="/public/events/guest-success" element={<GuestSuccessPage />} />
      <Route path="/public/survey/:surveyId" element={<PublicSurveyPage />} />
      <Route path="/public/marketplace/:listingId" element={<PublicListingPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/dashboard" element={
        <AdminRoute>
          <DashboardPage />
        </AdminRoute>
      } />
      <Route path="/home" element={
        <ProtectedRoute>
          <MemberDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/members" element={
        <ProtectedRoute>
          <MembersPage />
        </ProtectedRoute>
      } />
      <Route path="/members/:id" element={
        <ProtectedRoute>
          <MemberDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/status" element={
        <AdminRoute>
          <AdminStatusPage />
        </AdminRoute>
      } />
      <Route path="/admin/manage" element={
        <AdminRoute>
          <AdminManagePage />
        </AdminRoute>
      } />
      <Route path="/admin/changes" element={
        <AdminRoute>
          <AdminChangesPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter" element={
        <AdminRoute>
          <AdminNewsletterPage />
        </AdminRoute>
      } />
      <Route path="/admin/email-templates" element={
        <AdminRoute>
          <AdminEmailTemplatesPage />
        </AdminRoute>
      } />
      <Route path="/admin/directmail" element={
        <AdminRoute>
          <AdminDirectMailPage />
        </AdminRoute>
      } />
      <Route path="/admin/invoices" element={
        <AdminRoute>
          <InvoicesPage />
        </AdminRoute>
      } />
      <Route path="/admin/invoices/settings" element={
        <AdminRoute>
          <InvoiceSettingsPage />
        </AdminRoute>
      } />
      <Route path="/admin/stripe" element={
        <AdminRoute>
          <StripeFinancePage />
        </AdminRoute>
      } />
      <Route path="/admin/agb" element={
        <AdminRoute>
          <AdminAGBPage />
        </AdminRoute>
      } />
      <Route path="/admin/membership-requests" element={
        <AdminRoute>
          <AdminMembershipRequestsPage />
        </AdminRoute>
      } />
      <Route path="/admin/member-movements" element={
        <AdminRoute>
          <AdminMemberMovementsPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system/subscribers" element={
        <AdminRoute>
          <Navigate to="/admin/newsletter-groups" replace />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-subscriptions" element={
        <AdminRoute>
          <Navigate to="/admin/newsletter-groups" replace />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system" element={
        <AdminRoute>
          <NewsletterCampaignsPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system/new" element={
        <AdminRoute>
          <NewsletterEditorPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system/edit/:campaignId" element={
        <AdminRoute>
          <NewsletterEditorPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system/report/:campaignId" element={
        <AdminRoute>
          <NewsletterReportPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-system/analytics" element={
        <AdminRoute>
          <NewsletterAnalyticsPage />
        </AdminRoute>
      } />
      <Route path="/admin/documents" element={
        <AdminRoute>
          <AdminDocumentsPage />
        </AdminRoute>
      } />
      <Route path="/admin/sync" element={
        <AdminRoute>
          <AdminSyncPage />
        </AdminRoute>
      } />
      <Route path="/admin/activity-log" element={
        <AdminRoute>
          <AdminActivityLogPage />
        </AdminRoute>
      } />
      <Route path="/admin/newsletter-groups" element={
        <AdminRoute>
          <NewsletterGroupsPage />
        </AdminRoute>
      } />
      <Route path="/admin/surveys" element={
        <AdminRoute>
          <SurveysPage />
        </AdminRoute>
      } />
      <Route path="/admin/surveys/new" element={
        <AdminRoute>
          <SurveyEditPage />
        </AdminRoute>
      } />
      <Route path="/admin/surveys/:surveyId/edit" element={
        <AdminRoute>
          <SurveyEditPage />
        </AdminRoute>
      } />
      <Route path="/admin/surveys/:surveyId/results" element={
        <AdminRoute>
          <SurveyResultsPage />
        </AdminRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <DocumentsPage />
        </ProtectedRoute>
      } />
      <Route path="/my-invoices" element={
        <ProtectedRoute>
          <MyInvoicesPage />
        </ProtectedRoute>
      } />
      <Route path="/my-events" element={
        <ProtectedRoute>
          <MyOrganizedEventsPage />
        </ProtectedRoute>
      } />
      <Route path="/vehicles" element={
        <ProtectedRoute>
          <VehiclesPage />
        </ProtectedRoute>
      } />
      <Route path="/car-of-the-month" element={
        <ProtectedRoute>
          <CarOfTheMonthPage />
        </ProtectedRoute>
      } />
      <Route path="/about" element={
        <ProtectedRoute>
          <AboutPage />
        </ProtectedRoute>
      } />
      <Route path="/member-vehicles" element={
        <ProtectedRoute>
          <MemberVehiclesPage />
        </ProtectedRoute>
      } />
      <Route path="/marketplace" element={
        <ProtectedRoute>
          <MarketplacePage />
        </ProtectedRoute>
      } />
      <Route path="/marketplace/new" element={
        <ProtectedRoute>
          <ListingEditPage />
        </ProtectedRoute>
      } />
      <Route path="/marketplace/:listingId" element={
        <ProtectedRoute>
          <ListingDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/marketplace/:listingId/edit" element={
        <ProtectedRoute>
          <ListingEditPage />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/events/new" element={
        <AdminRoute>
          <EventEditPage />
        </AdminRoute>
      } />
      <Route path="/events/:id" element={
        <ProtectedRoute>
          <EventDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/events/:id/edit" element={
        <AdminRoute>
          <EventEditPage />
        </AdminRoute>
      } />
      <Route path="/events/:id/checkin" element={
        <AdminRoute>
          <EventCheckinPage />
        </AdminRoute>
      } />
      <Route path="/admin/checkin" element={
        <AdminRoute>
          <EventCheckinListPage />
        </AdminRoute>
      } />
      <Route path="/fahrzeug/:memberId/:vehicleId" element={
        <ProtectedRoute>
          <VehicleDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/profile/edit" element={
        <ProtectedRoute>
          <EditProfilePage />
        </ProtectedRoute>
      } />
      {/* Catch-all: unbekannte URLs zeigen 404 statt weisser Seite */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-background">
            <AppRoutes />
            <Toaster 
              position="top-right" 
              richColors 
              expand={true}
              closeButton={true}
            />
            <InstallPWABanner />
            <UpdateNotification />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
